import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppLayout from '../components/AppLayout'
import { useApp } from '../context/AppContext'

export default function CreateEvent() {
  const navigate = useNavigate()
  const { addEvent, currentUser, sendInvitation } = useApp()
  const [form, setForm] = useState({
    name: '',
    date: '',
    color: '#E8C1D1',
    description: '',
    location: '',
    dresscode: '',
    ownerName: '',
  })
  const [members, setMembers] = useState([])
  const [newMember, setNewMember] = useState({ name: '', email: '', role: 'helper' })

  const colors = [
    { hex: '#E8C1D1', name: 'Blush Pink' },
    { hex: '#C9845A', name: 'Dusty Rose' },
    { hex: '#C08A4A', name: 'Terracotta' },
    { hex: '#F7B98A', name: 'Apricot' },
    { hex: '#2271B1', name: 'Blue' },
    { hex: '#B4B48D', name: 'Olive Green' }
  ]

  const handleAddMember = () => {
    if (newMember.name && newMember.email) {
      setMembers(prev => [...prev, { ...newMember, id: Date.now().toString() }])
      setNewMember({ name: '', email: '', role: 'helper' })
    }
  }

  const handleRemoveMember = (id) => {
    setMembers(prev => prev.filter(m => m.id !== id))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name || !form.date || !form.ownerName) return

    try {
      // Create members array with owner and other members
      const eventMembers = [
        { id: currentUser.id, name: form.ownerName, role: 'owner' },
        ...members.map(m => ({ ...m, role: m.role }))
      ]

      const event = await addEvent({
        name: form.name,
        date: form.date,
        color: form.color,
        description: form.description,
        location: form.location,
        dresscode: form.dresscode,
        ownerName: form.ownerName,
        members: eventMembers,
      })

      // Send invitations to all members (non-blocking)
      Promise.all(
        members.map(member => sendInvitation(event.id, member.email, member.role, event).catch(() => {}))
      ).catch(() => {})

      // Give Firestore a moment to sync before navigating
      setTimeout(() => {
        navigate(`/event/${event.id}`)
      }, 500)
    } catch (error) {
      console.error('Error creating event:', error)
      alert('Fejl ved oprettelse af event')
    }
  }

  return (
    <AppLayout>
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">Opret Nyt Event</h1>
          </div>
        </div>
        <p style={{ fontSize: 12, color: '#DF83A9', marginBottom: 16 }}>Tilføj dine event-detaljer</p>
      </div>

      <div className="page-content">
        <form onSubmit={handleSubmit} style={{ maxWidth: 600 }}>
          {/* Event navn */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 16 }}>Event Navn</h2>
            <div className="input-group">
              <label className="input-label">Hvad kalder du eventet?</label>
              <input
                className="input"
                placeholder="fx. Min Fødselsdag"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
          </div>

          {/* Detaljer */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 16 }}>Detaljer</h2>

            <div className="input-group">
              <label className="input-label">Dato</label>
              <input
                className="input"
                type="date"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
                required
              />
            </div>

            <div className="input-group">
              <label className="input-label">Lokation</label>
              <input
                className="input"
                placeholder="fx. København"
                value={form.location}
                onChange={e => setForm({ ...form, location: e.target.value })}
              />
            </div>

            <div className="input-group">
              <label className="input-label">Dresscode</label>
              <input
                className="input"
                placeholder="fx. Formel / Smart Casual"
                value={form.dresscode}
                onChange={e => setForm({ ...form, dresscode: e.target.value })}
              />
            </div>

            <div className="input-group">
              <label className="input-label">Beskrivelse</label>
              <input
                className="input"
                placeholder="Tilføj evt. noter til eventet"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                style={{ minHeight: 80 }}
              />
            </div>

            <div className="input-group">
              <label className="input-label">Farve (Tuscan Sunset)</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {colors.map(c => (
                  <div
                    key={c.hex}
                    onClick={() => setForm({ ...form, color: c.hex })}
                    style={{
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: 80,
                        borderRadius: 12,
                        background: c.hex,
                        border: form.color === c.hex ? '3px solid #1a1a2e' : '2px solid #e0e0e0',
                        marginBottom: 8,
                      }}
                    />
                    <p style={{ fontSize: 11, fontWeight: 600, color: '#1a1a2e', textAlign: 'center' }}>{c.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Inviter medlemmer */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 16 }}>Inviter Medlemmer</h2>

            <div style={{ marginBottom: 16 }}>
              <div className="input-group" style={{ marginBottom: 8 }}>
                <label className="input-label">Navn</label>
                <input
                  className="input"
                  placeholder="Fuldt navn"
                  value={newMember.name}
                  onChange={e => setNewMember({ ...newMember, name: e.target.value })}
                />
              </div>

              <div className="input-group" style={{ marginBottom: 8 }}>
                <label className="input-label">Email</label>
                <input
                  className="input"
                  type="email"
                  placeholder="email@example.com"
                  value={newMember.email}
                  onChange={e => setNewMember({ ...newMember, email: e.target.value })}
                />
              </div>

              <div className="input-group" style={{ marginBottom: 8 }}>
                <label className="input-label">Rolle</label>
                <select
                  className="input"
                  value={newMember.role}
                  onChange={e => setNewMember({ ...newMember, role: e.target.value })}
                  style={{ appearance: 'none', paddingRight: 30 }}
                >
                  <option value="helper">Medhjælper</option>
                  <option value="co-owner">Med-ansvarlig</option>
                </select>
              </div>

              <button
                type="button"
                onClick={handleAddMember}
                className="btn-ghost"
                style={{ width: '100%', padding: '10px 0', marginTop: 8 }}
              >
                + Tilføj Medlem
              </button>
            </div>

            {members.length > 0 && (
              <div style={{ borderTop: '1.5px solid #f0eeff', paddingTop: 16 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e', marginBottom: 12 }}>Inviterede medlemmer ({members.length})</p>
                {members.map(member => (
                  <div key={member.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, padding: '10px 12px', background: '#f0eeff', borderRadius: 8 }}>
                    <div>
                      <p style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e' }}>{member.name}</p>
                      <p style={{ fontSize: 10, color: '#DF83A9' }}>{member.email} · {member.role === 'helper' ? 'Medhjælper' : 'Med-ansvarlig'}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveMember(member.id)}
                      style={{ background: 'none', border: 'none', color: '#ff6b6b', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}
                    >
                      Fjern
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Velg Arrangør */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 16 }}>Hvem er Arrangør?</h2>
            <div className="input-group">
              <label className="input-label">Velg arrangør</label>
              <select
                className="input"
                value={form.ownerName}
                onChange={e => setForm({ ...form, ownerName: e.target.value })}
                style={{ appearance: 'none', cursor: 'pointer' }}
              >
                <option value="">— vælg arrangør —</option>
                <option value={currentUser.name}>{currentUser.name} (dig selv)</option>
                {members.filter(m => m.role === 'co-owner').map(m => (
                  <option key={m.id} value={m.name}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button type="submit" className="btn-primary" style={{ flex: 1 }}>
              Opret Event
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="btn-ghost"
              style={{ flex: 1 }}
            >
              Annuller
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
